import React, { useEffect, useState, useMemo } from 'react';
import InputModal from '../../ui/inputModal/InputModal';
import { Users, Mail, Activity, Calendar, Edit, Trash2, KeyRound, CheckCircle, Clock, Ban } from 'lucide-react';
import styles from './UsersTab.module.css';
import { UsersTabProps } from '../../types/types';
import { RegistrationSource } from '../../enums/registrationSource';
import CreateUser from './createUser/CreateUser';
import EditUserModal from '../editUserModal/EditUserModal';
import { User } from '../../types/user';
import { toast } from '../../lib/toast';
import { useUsersStore } from '../../store/usersStore';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { UserRole } from '../../enums/userRole';
import { hasMinRole } from '../../lib/permissions';



const UsersTab: React.FC<UsersTabProps> = ({
  showCreateUser,
  setShowCreateUser,
  getRoleBadge
}) => {
  const currentUser = useAuthStore(state => state.user)
  const { users, deleteUser: deleteUserInStore, activateUser: activateUserInStore } = useUsersStore()
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'blocked'>('all');
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveUserId, setApproveUserId] = useState<string | null>(null);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [unblockUserId, setUnblockUserId] = useState<string | null>(null);
  const [pendingModalAction, setPendingModalAction] = useState<'delete' | 'approve' | 'unblock' | null>(null);
  const [editUserForm, setEditUserForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '' as string | null,
    role: null as number | null,
    is_active: true
  })

  // Filter users based on active tab
  const displayedUsers = useMemo(() => {
    if (activeTab === 'pending') {
      return users.filter(user => user.role?.id === UserRole.Pending);
    }
    if (activeTab === 'blocked') {
      return users.filter(user => user.role?.id === UserRole.Blocked);
    }
    return users.filter(user => user.role?.id !== UserRole.Pending && user.role?.id !== UserRole.Blocked);
  }, [users, activeTab]);

  const pendingUsersCount = useMemo(() => {
    return users.filter(user => user.role?.id === UserRole.Pending).length;
  }, [users]);

  const blockedUsersCount = useMemo(() => {
    return users.filter(user => user.role?.id === UserRole.Blocked).length;
  }, [users]);

  // Tab configuration array
  const tabs = useMemo(() => [
    {
      id: 'all' as const,
      label: 'All Users',
      icon: Users,
      count: users.filter(u => u.role?.id !== UserRole.Pending && u.role?.id !== UserRole.Blocked).length,
      showCount: true
    },
    {
      id: 'pending' as const,
      label: 'Pending Approval',
      icon: Clock,
      count: pendingUsersCount,
      showCount: pendingUsersCount > 0
    },
    {
      id: 'blocked' as const,
      label: 'Blocked Users',
      icon: Ban,
      count: blockedUsersCount,
      showCount: blockedUsersCount > 0
    }
  ], [users, pendingUsersCount, blockedUsersCount]);

  useEffect(() => {
    if (!currentUser || !hasMinRole(currentUser?.role?.id, UserRole.Admin)) {
      navigate('/');
    }
  }, [currentUser])


  const handleDelete = (userId: string) => {
    setDeleteUserId(userId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteUserId == null || pendingModalAction === 'delete') return;
    try {
      setPendingModalAction('delete');
      await deleteUser(deleteUserId);
      setShowDeleteModal(false);
      setDeleteUserId(null);
    } finally {
      setPendingModalAction(null);
    }
  };

  const startEditUser = (user: User) => {
    setEditingUser(user)
    setEditUserForm({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      password: '',
      role: user.role.id,
      is_active: user.is_active
    })
  }



  const deleteUser = async (userId: string) => {
    try {
      await deleteUserInStore(userId)
      toast.success('User deleted successfully!')
    } catch (error: any) {
      toast.error(`Failed to delete user: ${error.message}`)
    }
  }

  const handleApprove = (userId: string) => {
    setApproveUserId(userId);
    setShowApproveModal(true);
  };

  const handleConfirmApprove = async () => {
    if (approveUserId == null || pendingModalAction === 'approve') return;
    try {
      setPendingModalAction('approve');
      // Activate user with default Viewer role
      await activateUserInStore(approveUserId, UserRole.Viewer);
      toast.success('User approved successfully!');
      // If we're on pending tab and no more pending users, switch to all tab
      if (activeTab === 'pending' && pendingUsersCount <= 1) {
        setActiveTab('all');
      }
      setShowApproveModal(false);
      setApproveUserId(null);
    } catch (error: any) {
      toast.error(`Failed to approve user: ${error.message}`);
    } finally {
      setPendingModalAction(null);
    }
  };

  const handleUnblock = (userId: string) => {
    setUnblockUserId(userId);
    setShowUnblockModal(true);
  };

  const handleConfirmUnblock = async () => {
    if (unblockUserId == null || pendingModalAction === 'unblock') return;
    try {
      setPendingModalAction('unblock');
      // Activate user with default Viewer role to unblock them
      await activateUserInStore(unblockUserId, UserRole.Viewer);
      toast.success('User unblocked successfully!');
      // If we're on blocked tab and no more blocked users, switch to all tab
      if (activeTab === 'blocked' && blockedUsersCount <= 1) {
        setActiveTab('all');
      }
      setShowUnblockModal(false);
      setUnblockUserId(null);
    } catch (error: any) {
      toast.error(`Failed to unblock user: ${error.message}`);
    } finally {
      setPendingModalAction(null);
    }
  };


  return (
    <div className={styles.usersTab}>
      <CreateUser showCreateUser={showCreateUser} setShowCreateUser={setShowCreateUser} />
      
      {/* Tabs */}
      <div className={styles.tabs}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={16} />
              {tab.label} {tab.showCount && `(${tab.count})`}
            </button>
          );
        })}
      </div>

      {/* Users List */}
      {displayedUsers.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.logo}>
            {activeTab === 'pending' ? <Clock size={48} /> : activeTab === 'blocked' ? <Ban size={48} /> : <Users size={48} />}
          </div>
          <h3>
            {activeTab === 'pending' ? 'No pending users' : activeTab === 'blocked' ? 'No blocked users' : 'No users found'}
          </h3>
          <p>
            {activeTab === 'pending' 
              ? 'All users have been approved' 
              : activeTab === 'blocked'
              ? 'No users are currently blocked'
              : 'Add your first user to get started'}
          </p>
        </div>
      ) : (
        <div className={styles.usersList}>
          {displayedUsers.map((user) => (
            <div key={user.id} className={styles.userCard}>
              <div className={styles.userInfo}>
                <div className={styles.userAvatar}>
                  {user.first_name[0]}{user.last_name[0]}
                </div>
                <div className={styles.userDetails}>
                  <h3>{user.first_name} {user.last_name}</h3>
                  <p className={styles.userEmail}>
                    <Mail size={14} />
                    {user.email}
                  </p>
                  <div className={styles.userMeta}>
                    {getRoleBadge(user.role?.id)}
                    {activeTab === 'all' && (
                      <>
                        <span className={styles.userStatus}>
                          <Activity size={12} />
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {user.last_login && (
                          <span className={styles.lastLogin}>
                            <Calendar size={12} />
                            Last login: {new Date(user.last_login).toLocaleDateString()}
                          </span>
                        )}
                      </>
                    )}
                    {user.registrationSource && (
                      <span className={styles.lastLogin}>
                        <KeyRound size={12} />
                        {user.registrationSource === 'idp' ? RegistrationSource.Idp : 'local'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className={styles.userActions}>
                {activeTab === 'pending' ? (
                  <>
                    <button
                      onClick={() => handleApprove(user.id)}
                      className={styles.actionBtn}
                      title="Approve user"
                    >
                      <CheckCircle size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className={styles.actionBtn}
                      title="Remove user"
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                ) : activeTab === 'blocked' ? (
                  <>
                    <button
                      onClick={() => handleUnblock(user.id)}
                      className={styles.actionBtn}
                      title="Unblock user"
                    >
                      <CheckCircle size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className={styles.actionBtn}
                      title="Delete user"
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => startEditUser(user)} className={styles.actionBtn}>
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className={styles.actionBtn}
                      disabled={user.id === currentUser?.id}
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <InputModal
        open={showDeleteModal}
        title={activeTab === 'pending' ? "Remove Pending User" : "Delete User"}
        confirmText={activeTab === 'pending' ? "Remove" : "Delete"}
        cancelText="Cancel"
        showInput={false}
        message={
          activeTab === 'pending'
            ? "Are you sure you want to remove this pending user? This action cannot be undone."
            : "Are you sure you want to delete this user? This action cannot be undone."
        }
        loading={pendingModalAction === 'delete'}
        loadingText={activeTab === 'pending' ? 'Removing...' : 'Deleting...'}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (pendingModalAction !== 'delete') {
            setShowDeleteModal(false);
            setDeleteUserId(null);
          }
        }}
      />

      <InputModal
        open={showApproveModal}
        title="Approve User"
        confirmText="Approve"
        cancelText="Cancel"
        showInput={false}
        message="Are you sure you want to approve this user? They will be assigned the Viewer role and gain access to the system."
        loading={pendingModalAction === 'approve'}
        loadingText="Approving..."
        onConfirm={handleConfirmApprove}
        onCancel={() => {
          if (pendingModalAction !== 'approve') {
            setShowApproveModal(false);
            setApproveUserId(null);
          }
        }}
      />

      <InputModal
        open={showUnblockModal}
        title="Unblock User"
        confirmText="Unblock"
        cancelText="Cancel"
        showInput={false}
        message="Are you sure you want to unblock this user? They will be assigned the Viewer role and regain access to the system."
        loading={pendingModalAction === 'unblock'}
        loadingText="Unblocking..."
        onConfirm={handleConfirmUnblock}
        onCancel={() => {
          if (pendingModalAction !== 'unblock') {
            setShowUnblockModal(false);
            setUnblockUserId(null);
          }
        }}
      />

      <EditUserModal
        editingUser={editingUser}
        setEditingUser={setEditingUser}
        editUserForm={editUserForm}
        setEditUserForm={setEditUserForm}
        currentUser={currentUser}
      />
    </div>
  );
};

export default UsersTab;
